import { Alert } from '@creditornot/cb-alert';
import { InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { Radio } from '@creditornot/cb-radio';
import { FORM_ERROR } from 'final-form';
import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { deleteMenuFile, patchInboundMerchant, uploadMenuFile } from 'apis/merchant-onboarding-service/api';
import { RFFFieldErrorWrapper } from 'common/components';
import { Typography as T } from 'common/typography';
import validateYupSchema from 'common/utils/validateYupSchema';

import { DocumentUploader } from './DocumentUploader/DocumentUploader';
import { ProductList, productListValidationSchema } from './validation';
import { SelfServeStepsIdentifiers } from '../constants';
import { validateStepParam } from '../helpers';
import { InboundMerchant, SsmoMenuFile } from '../types';

type ProductListFormProps = {
  inboundMerchant: InboundMerchant;
  goToNextStep: () => void;
  setUpdateIsInProgress: (inProgress: boolean) => void;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-block-end: 32px;
`;

const StyledRadio = styled(Radio)`
  margin-block-end: 16px;
`;

const StyledDescription = styled(T.Body3)`
  margin-block-start: 8px;
  margin-block-end: 32px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const createEmptyFileArrayFromMenuFiles = (menuFiles: SsmoMenuFile[]): File[] => {
  if (!menuFiles || menuFiles.length === 0) {
    return [];
  }

  const emptyMenuFiles = menuFiles.map(menuFile => {
    const buffer = new Uint8Array(menuFile.sizeInBytes).fill(0);
    const blob = new Blob([buffer], { type: 'text/plain' });
    const file = new File([blob], menuFile.name, { type: 'text/plain' });

    return file;
  });

  return emptyMenuFiles;
};

const ProductListForm = ({ inboundMerchant, goToNextStep, setUpdateIsInProgress }: ProductListFormProps) => {
  const { search } = useLocation();
  const { formatMessage } = useIntl();

  const searchParams = new URLSearchParams(search);
  const merchantId = searchParams.get('merchantId') || '';

  const [uploadedFiles, setUploadedFiles] = useState<File[]>(
    createEmptyFileArrayFromMenuFiles(inboundMerchant.ssmoMenuFiles || []),
  );
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [failedToUpload, setFailedToUpload] = useState(false);
  const [fileBeingDeletedName, setFileBeingDeletedName] = useState<string | null>(null);

  const initialValues: ProductList = useMemo(() => {
    const { ssmoMenuFileLink } = inboundMerchant;

    if (ssmoMenuFileLink) {
      return { source: 'link', link: ssmoMenuFileLink };
    }

    return { source: 'file', link: '' };
  }, [inboundMerchant]);

  const handleOnFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setIsUploadingFile(true);
      setUpdateIsInProgress(true);
      await uploadMenuFile(formData);

      setUploadedFiles([...uploadedFiles, file]);
      setFailedToUpload(false);
    } catch (error) {
      setFailedToUpload(true);

      console.error('Error uploading file:', error);
    } finally {
      setIsUploadingFile(false);
      setUpdateIsInProgress(false);
    }
  };

  const handleOnDeleteFile = async (file: File) => {
    const fileName = file.name;

    try {
      setUpdateIsInProgress(true);
      setFileBeingDeletedName(fileName);
      await deleteMenuFile(fileName);
      setFileBeingDeletedName(null);

      const newFiles = uploadedFiles.filter(uploadedFile => uploadedFile !== file);
      setUploadedFiles(newFiles);
    } catch (error) {
      setFileBeingDeletedName(null);
      console.error('Error deleting file:', error);
    } finally {
      setUpdateIsInProgress(false);
    }
  };

  const handleSubmitProductListForm = async (values: ProductList) => {
    const isLinkSource = values.source === 'link';

    if (isLinkSource) {
      const validFormValues = productListValidationSchema.validateSync(values);

      try {
        const stepParam = validateStepParam(searchParams.get('step'));

        await patchInboundMerchant(
          { ssmoMenuFileLink: validFormValues?.link ?? '', ssmoStepCompletionId: stepParam },
          merchantId,
        );

        goToNextStep();
        return;
      } catch (submitError) {
        return { [FORM_ERROR]: submitError as Error };
      }
    } else {
      goToNextStep();
      return;
    }
  };

  return (
    <Wrapper>
      <StyledDescription>
        {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.description' })}
      </StyledDescription>

      <Form<ProductList>
        initialValues={initialValues}
        onSubmit={handleSubmitProductListForm}
        validate={validateYupSchema(productListValidationSchema)}
      >
        {({ handleSubmit, values }) => {
          const isFileSource = values.source === 'file';

          return (
            <StyledForm id={SelfServeStepsIdentifiers.ProductList} onSubmit={handleSubmit}>
              <Field<ProductList['source']> name="source">
                {({ input }) => (
                  <RadioGroup>
                    <StyledRadio
                      {...input}
                      checked={values.source === 'file'}
                      onChange={e => {
                        e.preventDefault();
                        input.onChange('file');
                      }}
                    >
                      {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.upload-menu-file' })}
                    </StyledRadio>
                    <StyledRadio
                      {...input}
                      value={input.value?.toString()}
                      checked={values.source === 'link'}
                      onChange={e => {
                        e.preventDefault();
                        input.onChange('link');
                      }}
                    >
                      {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.add-menu-link' })}
                    </StyledRadio>
                  </RadioGroup>
                )}
              </Field>

              {isFileSource ? (
                <>
                  <InputContainer>
                    <DocumentUploader
                      uploadedFiles={uploadedFiles}
                      onChange={handleOnFileUpload}
                      isUploading={isUploadingFile}
                      failedToUpload={failedToUpload}
                      onDeletedFile={handleOnDeleteFile}
                      fileBeingDeletedName={fileBeingDeletedName}
                    />
                  </InputContainer>

                  <Alert
                    title={formatMessage({ id: 'self-serve-sign-up.wizard.product-list.alert-title' })}
                    variant="info"
                  >
                    {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.alert-text' })}
                  </Alert>
                </>
              ) : (
                <Field<ProductList['link']> name="link">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <InputContainer
                          label={formatMessage({ id: 'self-serve-sign-up.wizard.product-list.menu-link' })}
                        >
                          <Input {...input} type="text" placeholder="https://" />
                        </InputContainer>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              )}
            </StyledForm>
          );
        }}
      </Form>
    </Wrapper>
  );
};

export default ProductListForm;
